import React, { useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { useApi } from "../../hooks/useApi.js";
import { useAuth } from "../../hooks/useAuth.jsx";
import ClientBreadcrumb from "../../components/ClientBreadcrumb.jsx";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import ErrorBanner from "../../components/ErrorBanner.jsx";
import StatusFlow from "../../components/coordinator/StatusFlow.jsx";
import ItemEditor from "../../components/coordinator/ItemEditor.jsx";
import PeopleEditor from "../../components/coordinator/PeopleEditor.jsx";
import GoalEditor from "../../components/coordinator/GoalEditor.jsx";
import RoutineEditor from "../../components/coordinator/RoutineEditor.jsx";
import PhotoUpload from "../../components/PhotoUpload.jsx";
import SectionImagePanel from "../../components/coordinator/SectionImagePanel.jsx";
import ImagePicker from "../../components/coordinator/ImagePicker.jsx";
import AutoTextarea from "../../components/AutoTextarea.jsx";
import SectionPlaythroughPanel from "../../components/coordinator/SectionPlaythroughPanel.jsx";
import { sectionLabel } from "../../lib/sections.js";

const SLUG_TO_TYPE = {
  "meet": "meet", "communicate": "communicate", "sensory": "sensory",
  "passions": "passions", "day": "day", "working-on": "working_on",
  "hard-times": "hard_times", "health": "health", "people": "people",
  "community": "community",
};

const ITEM_SECTIONS = {
  communicate: "communication",
  sensory:     "sensory",
  passions:    "passion",
  hard_times:  "support",
  community:   "community",
};

// Count draft records in the section response
function countDraftRecords(section) {
  if (!section) return 0;
  return [
    ...(section.items || []),
    ...(section.content_blocks || []),
    ...(section.people || []),
    ...(section.goals || []),
    ...(section.routines || []),
    ...(section.medications || []),
  ].filter(r => r.status === "draft").length;
}

// ── Hero photo uploader (Meet section only) ───────────────────────────────────

function HeroPhotoUploader({ clientId }) {
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  React.useEffect(() => {
    fetch(`/api/clients/${clientId}`)
      .then(r => r.json())
      .then(data => {
        if (data.hero_photo_media_id) {
          setCurrentPhotoUrl(`/api/clients/${clientId}/media/${data.hero_photo_media_id}?v=display`);
        }
      })
      .catch(() => {});
  }, [clientId]);

  async function attach(item) {
    // Accept the full media object (ImagePicker.onSelect) or a bare id
    // (PhotoUpload.onUploaded passes data.id).
    const mediaId = (item && typeof item === "object") ? item.id : item;
    if (!mediaId) return;
    try {
      const res = await fetch(`/api/clients/${clientId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hero_photo_media_id: mediaId }),
      });
      if (res.ok) setCurrentPhotoUrl(`/api/clients/${clientId}/media/${mediaId}?v=display`);
    } catch (e) { console.error("Failed to attach hero photo:", e); }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "var(--space-4)", marginBottom: "var(--space-2)" }}>
      <PhotoUpload
        currentUrl={currentPhotoUrl}
        uploadUrl={`/api/clients/${clientId}/media`}
        onUploaded={data => attach(data.id)}
        size="lg"
      />
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}>
        <p style={{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)" }}>Hero photo</p>
        <p style={{ fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" }}>Shown on Meet page and stream.</p>
        <button
          style={{ fontSize: "var(--text-xs)", color: "var(--color-accent)", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
          onClick={() => setPickerOpen(true)}
        >
          Pick from library ↗
        </button>
      </div>
      {pickerOpen && (
        <ImagePicker
          clientId={clientId}
          onSelect={id => { attach(id); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ── MetaEditor — autosaves on blur ────────────────────────────────────────────

function MetaEditor({ clientId, sectionType, section }) {
  const [lead, setLead] = useState(section.lead_paragraph || "");
  const [teaser, setTeaser] = useState(section.tile_teaser || "");
  const [oneliner, setOneliner] = useState("");
  const [saveState, setSaveState] = useState(null);

  // Load hero_oneliner for Meet section
  React.useEffect(() => {
    if (sectionType === "meet") {
      fetch(`/api/clients/${clientId}`)
        .then(r => r.json())
        .then(data => setOneliner(data.hero_oneliner || ""))
        .catch(() => {});
    }
  }, [clientId, sectionType]);

  async function saveSection(field, value) {
    setSaveState("saving");
    try {
      await fetch(`/api/clients/${clientId}/sections/${sectionType}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      setSaveState("saved");
      setTimeout(() => setSaveState(null), 2000);
    } catch {
      setSaveState(null);
    }
  }

  async function saveClient(field, value) {
    setSaveState("saving");
    try {
      await fetch(`/api/clients/${clientId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      setSaveState("saved");
      setTimeout(() => setSaveState(null), 2000);
    } catch {
      setSaveState(null);
    }
  }

  return (
    <div style={styles.metaCard}>
      <div style={styles.metaHeader}>
        <h3 style={styles.metaHeading}>Section text</h3>
        {saveState === "saving" && <span style={styles.saveHint}>Saving…</span>}
        {saveState === "saved"  && <span style={{ ...styles.saveHint, color: "var(--color-status-live)" }}>Saved ✓</span>}
      </div>
      {sectionType === "meet" && <HeroPhotoUploader clientId={clientId} />}
      {sectionType === "meet" && (
        <div style={styles.field}>
          <label style={styles.label}>Hero one-liner <span style={{ color: "var(--color-critical)" }}>*</span></label>
          <input
            style={styles.input}
            value={oneliner}
            onChange={e => setOneliner(e.target.value)}
            onBlur={() => saveClient("hero_oneliner", oneliner)}
            placeholder="One sentence that captures who this person is (required for stream)"
          />
        </div>
      )}
      <div style={styles.field}>
        <label style={styles.label}>Opening paragraph</label>
        <AutoTextarea
          style={styles.textarea}
          value={lead}
          onChange={e => setLead(e.target.value)}
          onBlur={() => saveSection("lead_paragraph", lead)}
          rows={4}
          placeholder="Intro paragraph for support staff, for this section"
        />
      </div>
      <div style={styles.field}>
        <label style={styles.label}>Tile teaser (shown on Meet page)</label>
        <input
          style={styles.input}
          value={teaser}
          onChange={e => setTeaser(e.target.value)}
          onBlur={() => saveSection("tile_teaser", teaser)}
          placeholder="One line shown on the section tile"
        />
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function SectionEditor() {
  const { clientId, sectionSlug } = useParams();
  const { user } = useAuth();
  const sectionType = SLUG_TO_TYPE[sectionSlug];
  const [sectionStatus, setSectionStatus] = useState(null);
  const [draftCount, setDraftCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const { data: section, loading, error, refetch } = useApi(
    sectionType ? `/clients/${clientId}/sections/${sectionType}?include_draft=1` : null
  );

  // Client name for the breadcrumb. The section endpoint (SectionDetailSchema)
  // does NOT carry client_name, so reading section.client_name always fell back
  // to "Client". Fetch the bare client record (lighter than /profile) for it.
  const { data: client } = useApi(`/clients/${clientId}`);
  // The person's pronouns in the name ("His Day"), Oct 25.
  const label = sectionType ? sectionLabel(sectionType, client?.pronouns) : sectionSlug;

  // Sync draft count from section data on load
  React.useEffect(() => {
    if (section) {
      setSectionStatus(section.status);
      setDraftCount(countDraftRecords(section));
    }
  }, [section]);

  const refresh = useCallback(() => {
    setRefreshKey(k => k + 1);
    refetch();
  }, [refetch]);

  function handleStatusChange(newStatus, newDraftCount) {
    setSectionStatus(newStatus);
    setDraftCount(newDraftCount ?? countDraftRecords(section));
  }

  if (!sectionType) return <ErrorBanner message={`Unknown section: ${sectionSlug}`} />;
  if (loading) return <LoadingSpinner message={`Loading ${label}…`} />;
  if (error)   return <ErrorBanner message={error} onRetry={refetch} />;
  if (!section) return null;

  const currentStatus = sectionStatus || section.status;
  const itemCategory = ITEM_SECTIONS[sectionType];
  const hasItems  = sectionType in ITEM_SECTIONS;
  const hasPeople = sectionType === "people";
  const hasGoals  = sectionType === "working_on";

  return (
    <div style={styles.page}>
      <div style={styles.content}>

        <div style={styles.nav}>
          <ClientBreadcrumb
            clientName={client?.display_name}
            role={user?.role}
            clientHref={`/coordinator/clients/${clientId}/edit`}
            trail={[{ label }]}
          />
          <Link
            to={(sectionType === "meet" ? `/clients/${clientId}` : `/clients/${clientId}/${sectionSlug}`) + "?preview=1"}
            style={styles.previewLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            Preview ↗
          </Link>
        </div>

        <div style={styles.header}>
          <h1 style={styles.title}>{label}</h1>
          <StatusFlow
            clientId={clientId}
            sectionType={sectionType}
            currentStatus={currentStatus}
            draftCount={draftCount}
            onStatusChange={handleStatusChange}
          />
        </div>

        <MetaEditor clientId={clientId} sectionType={sectionType} section={section} />

        {/* Renders nothing while section play-throughs are switched off. */}
        <SectionPlaythroughPanel clientId={clientId} sectionType={sectionType} pronouns={client?.pronouns} />

        {/* Images panel — section strip */}
        <SectionImagePanel clientId={clientId} sectionSlug={sectionSlug} />

        {hasItems && (
          <ItemEditor
            key={`items-${refreshKey}`}
            clientId={clientId}
            sectionType={sectionType}
            sectionCategory={itemCategory}
            initialItems={section.items || []}
            onRefresh={refresh}
          />
        )}
        {hasPeople && (
          <PeopleEditor
            key={`people-${refreshKey}`}
            clientId={clientId}
            initialPeople={section.people || []}
            onRefresh={refresh}
          />
        )}
        {hasGoals && (
          <GoalEditor
            key={`goals-${refreshKey}`}
            clientId={clientId}
            initialGoals={section.goals || []}
            onRefresh={refresh}
          />
        )}
        {sectionType === "day" && (
          <RoutineEditor
            key={`routines-${refreshKey}`}
            clientId={clientId}
            initialRoutines={section.routines || []}
            onRefresh={refresh}
          />
        )}

      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100dvh", background: "var(--color-bg-subtle)", padding: "var(--space-6)" },
  content: { maxWidth: "var(--content-max-width)", margin: "0 auto", display: "flex", flexDirection: "column", gap: "var(--space-6)" },
  nav: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  previewLink: { fontSize: "var(--text-sm)", color: "var(--color-accent)", textDecoration: "none" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-6)", flexWrap: "wrap" },
  title: { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-bold)", color: "var(--color-text-primary)" },
  metaCard: { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  metaHeader: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  metaHeading: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-secondary)" },
  saveHint: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  field: { display: "flex", flexDirection: "column", gap: "var(--space-1)" },
  label: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", color: "var(--color-text-tertiary)" },
  input: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%" },
  textarea: { fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", background: "var(--color-bg-base)", color: "var(--color-text-primary)", width: "100%", resize: "vertical", fontFamily: "inherit" },
};
